import React, { useState, useEffect, useRef, useMemo } from "react";
import SyntheticControlStudy from "./SyntheticControlStudy";
import PostTestControlMarketSelection from "./PostTestControlMarketSection";
import ViewTestAndControlMarket from "./ViewTestAndControlMarket";
import { Steps, Button, Spin, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Papa from "papaparse";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PreCampaign from "./PreCampaign";
import VerifyTestInput from "./VerifyTestInput";
import ViewResults from "./ViewResults";
import axiosInstance from "../../../../utils/axiosInstance";
import { GeoLiftJobConfigService } from "../../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import { getTenantId } from "../../../../utils/auth";
import { v4 as uuidv4 } from "uuid";
import { JobType } from "../../../../constants/JobTypes";
import { uploadFileToS3 } from "../../../../utils/s3Upload";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../../../redux/store";
import type { AppDispatch } from "../../../../redux/store";
import AIAgentInsights from "../../../../components/agents/QcAgent/AIAgentInsights";
import { DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD } from "../../../../constants/common";
import { fetchJobConfigList } from "../../../../redux/geoLiftSlice";
import { CheckCircleFilled } from "@ant-design/icons";

dayjs.extend(customParseFormat);

interface MarketData {
  Holdout: number;
  ID: number;
  location: string;
  duration: number;
  EffectSize: number;
  Power: number;
  AvgScaledL2Imbalance: number;
  Investment: number;
  AvgATT: number;
  Average_MDE: number;
  ProportionTotal_Y: number;
  rank: number;
  abs_lift_in_perc: number;
  correlation: number;
}
const GeoLiftStudy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const geoLiftJobConfigService = new GeoLiftJobConfigService();
  const urlId = useParams<{ id: string }>();
  const rawId = location?.state?.jobConfigId ?? urlId.id;
  // "/experiments/new" uses "new" as a route placeholder — treat it as undefined so a fresh UUID is generated
  const id = rawId === "new" ? undefined : rawId;
  const dispatch = useDispatch<AppDispatch>();
  const JobName = location?.state?.jobName;
  const jobModelParameterConfig = location?.state?.modelParameterConfig;
  const [current, setCurrent] = useState(0);
  const verifyTestInputRef = useRef<{ submitForm: () => void } | null>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const scrollContentToTop = () => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
  };
  const [testMarkets, setTestMarkets] = useState<string[]>([]);
  const [jobId, setJobId] = useState<string>(id || "");
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const [selectedMarketsIncludes, setSelectedMarketsIncludes] = useState<
    string[]
  >([]);
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [postTestData, setPostTestData] = useState<any>(null);
  const [allMarketData, setAllMarketData] = useState<
    Record<string, MarketData[]>
  >({});
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const edit = searchParams.get("edit");
  const isManualCreate = searchParams.get("manual") === "true";
  const isEditMode = edit === "true";
  const isViewMode = !mode && !isEditMode && !!id;
  const isCreateMode = !!mode && !isEditMode;
  const [scmType, setScmType] = useState<string>("single");
  const [fileUploaded, setFileUploaded] = useState(false);
  const preCampaignRef = useRef<any>(null);
  const [fileData, setFileData] = useState<string | undefined>(undefined);
  const [campaignDate, setCampaignDate] = useState<Dayjs | null>(null);
  // Baseline snapshot taken directly from Redux job config (DB source of truth).
  // Using form state for the baseline caused a race: onValuesChange propagates
  // bauSpent/statSigThreshold one render AFTER the markets/campaignDate already
  // satisfied the old capture condition, so defaults (0/80) were snapped and
  // the real DB values looked like user edits.
  const [initialJobConfig, setInitialJobConfig] = useState<any>(null);
  const [isSales, setIsSales] = useState<boolean>(jobModelParameterConfig?.parameters?.isSales ?? true);



  const getNextButtonLabel = () => {
    const isEvaluateMode = mode === "evaluate";
    const currentStepId = steps[current]?.id;

    if (currentStepId === "results") return "View AI Insights";
    // Creating or editing: the upload step submits and leaves the wizard
    // entirely (handleNext redirects to /experiments) rather than advancing
    // through the multi-step browser, which is reserved for view mode.
    if (currentStepId === "upload" && !isViewMode) return "Submit";
    if (!isEvaluateMode && currentStepId === "verify") return "Create Control & Test Market";
    return "Next";
  };
  const [csvData, setCsvData] = useState<any[]>([]);
  const [refreshMarketData, setRefreshMarketData] = useState(0);

  const recommendationStatus = searchParams.get("recStatus");
  const buyType = location.state?.buyType;
  const [resolvedBuyType, setResolvedBuyType] = useState<string | undefined>(location.state?.buyType);
  const [columnMappings, setColumnMappings] = useState<{
    dateColumn: string;
    locationColumn: string;
    kpiColumn: string;
  }>({
    dateColumn: "date",
    locationColumn: "location",
    kpiColumn: "Y",
  });
  const [columnMappingsLoaded, setColumnMappingsLoaded] = useState(false);

  const isNextDisabled = () => {
    const stepId = steps[current]?.id;
    switch (stepId) {
      case "upload": {
        // In edit mode the button is the "Update Job" submit — keep it disabled
        // until the user actually modifies at least one field.
        if (isEditMode && !hasDataChanged && !isOnlyNameChanged) {
          return true;
        }
        const baseValidation =
          !fileUploaded ||
          !preCampaignData.geoLiftStudyName ||
          preCampaignData.geoLiftStudyName.trim() === "" ||
          selectedMarketsIncludes.length === 0;
        if (preCampaignData.buyType === "national") {
          return baseValidation || !preCampaignData.campaignStartDate;
        }
        return baseValidation;
      }

      case "verify":
        return (
          !csvData.length ||
          !columnMappings.dateColumn ||
          !columnMappings.locationColumn ||
          !columnMappings.kpiColumn
        );

      case "power":
        return testMarkets.length === 0 || !selectedMarket;

      default:
        return false;
    }
  };
  const [preCampaignData, setPreCampaignData] = useState<{
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string; // or Dayjs if not formatted
    totalSpent?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
  }>({
    geoLiftStudyName: JobName || "",
    buyType: buyType || "national",
    fileList: [],
    is_ai_qc_enabled: false,
    bauSpent: 0,
    statSigThreshold: DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD,
    isSales: jobModelParameterConfig?.parameters?.isSales,
  });

  useEffect(() => {
    const checkPostTestData = async () => {
      if (!jobId) return;

      // Skip post-test data check if this is a new job
      // New jobs have mode=evaluate or mode=plan in URL
      const modeParam = searchParams.get("mode");
      const isNewJob = !!modeParam;
      console.log("checkPostTestData - mode:", modeParam, "isNewJob:", isNewJob, "jobId:", jobId);
      if (isNewJob) {
        console.log("Skipping checkPostTestData for new job");
        return;
      }

      // Skip if in edit mode - user is actively editing, not viewing results
      const isEditMode = searchParams.get("edit") === "true";
      if (isEditMode) {
        console.log("Skipping checkPostTestData for edit mode");
        return;
      }

      try {
        // For recommendation view, skip post-test file check and directly fetch output
        const isViewingRecommendation =
          recommendationStatus === "recommendation_success";
        const postFileResponse = !isViewingRecommendation
          ? await geoLiftJobConfigService.getFile(
              `${jobId}/input/geo_lift_post_data.csv`
            )
          : true; // Bypass file check for recommendations

        if (postFileResponse || isViewingRecommendation) {
          const response = await axiosInstance.get(
            `/api/geo-lift/output/${jobId}`
          );
          if (response.data && response.data.result) {
            const resultData = response.data.result;
            const liftPlot = resultData.lift_plot || {};
            const attPlot = resultData.att_plot || {};
            const tObsValues = Object.values(liftPlot.t_obs || {});
            const cObsValues = Object.values(liftPlot.c_obs || {});
            const salesWithCampaignData = tObsValues.map((val: any) => {
              const num = Number(val);
              return isNaN(num) ? 0 : num;
            });

            const salesWithoutCampaignData = cObsValues.map((val: any) => {
              const num = Number(val);
              return isNaN(num) ? 0 : num;
            });

            // Calculate total sales
            const totalSalesWithCampaign = salesWithCampaignData.reduce(
              (sum: number, val: number) => sum + val,
              0
            );
            const totalSalesWithoutCampaign = salesWithoutCampaignData.reduce(
              (sum: number, val: number) => sum + val,
              0
            );

            // Calculate lift percentage
            const liftPercentage =
              totalSalesWithoutCampaign > 0
                ? ((totalSalesWithCampaign - totalSalesWithoutCampaign) /
                    totalSalesWithoutCampaign) *
                  100
                : 0;

            // Calculate ROAS and incremental sales
            const incrementalSales =
              totalSalesWithCampaign - totalSalesWithoutCampaign;
            const adSpend = resultData.adSpend || 0;
            const roas = adSpend > 0 ? incrementalSales / adSpend : 0;

            const formattedData = {
              lift_plot: {
                Time: liftPlot.Time || {},
                t_obs: liftPlot.t_obs || {},
                c_obs: liftPlot.c_obs || {},
              },
              att_plot: {
                Time: attPlot.Time || {},
                Estimate: attPlot.Estimate || {},
              },
              salesWithoutCampaign: totalSalesWithoutCampaign,
              pvalue: resultData.pvalue || 0,
              salesWithCampaign: totalSalesWithCampaign,
              lift_percentage: liftPercentage,
              incremental: incrementalSales,
              adSpend: adSpend,
              roas: roas,
              qc_openai: resultData['qc_openai'],
              isSales,
            };
            setPostTestData(formattedData);

            // Get the test markets from the API response
            const locations = Object.keys(response.data.result)
              .filter((key) => key.includes("/BestMarkets"))
              .map((key) => key.split("/")[0]);

            if (locations.length > 0) {
              setTestMarkets(locations);
              // Set the first location as selected by default
              setSelectedMarket(locations[0]);

              // Get market data for the selected location
              const marketDataMap: Record<string, MarketData[]> = {};
              locations.forEach((location) => {
                const key = `${location}/BestMarkets`;
                if (response.data.result[key]) {
                  marketDataMap[location] = response.data.result[key];
                }
              });
              setAllMarketData(marketDataMap);
              setMarketData(marketDataMap[locations[0]] || []);
            }
          }
        }
      } catch (error) {
        console.error("Error checking post-test data:", error);
      }
    };

    // Only call checkPostTestData if not a new job and not in edit mode
    const modeParamResult = searchParams.get("mode");
    const isEditModeResult = searchParams.get("edit") === "true";
    const isNewJobResult = !!modeParamResult && !isEditModeResult; // New job has mode but NOT edit=true

    console.log("GeoLiftStudy useEffect - mode:", modeParamResult, "edit:", isEditModeResult, "isNewJob:", isNewJobResult, "jobId:", jobId);

    if (!isNewJobResult && !isEditModeResult) {
      console.log("Calling checkPostTestData in GeoLiftStudy - view mode");
      checkPostTestData();
    } else {
      console.log("Skipping checkPostTestData in GeoLiftStudy - new job or edit mode");
    }
  }, [jobId, searchParams]);

  useEffect(() => {
    if (recommendationStatus && recommendationStatus !== "null") {
      if (recommendationStatus === "recommendation_success") {
        setCurrent(2);
      } else {
        setCurrent(steps.length - 1);
      }
    }
  }, [recommendationStatus]);

  // Fetch buyType from the job config API when it's not available from navigation state.
  // This happens when the URL is opened directly (e.g., copied into a new tab).
  useEffect(() => {
    if (!isViewMode || resolvedBuyType !== undefined || !jobId) return;
    axiosInstance.get(`/api/jobConfig/${jobId}`)
      .then(response => {
        const bt = response.data?.modelParameterConfig?.parameters?.buyType || "non-national";
        setResolvedBuyType(bt);
      })
      .catch(() => setResolvedBuyType("non-national"));
  }, [isViewMode, jobId]);

  // Load column mappings and capture initial data for edit/view mode
  const jobConfigList = useSelector((state: RootState) => state.geoLift.jobConfigList);

  // The canonical study name, sourced from Redux jobConfigList once it's
  // loaded. PreCampaign's local state round-trips through onValuesChange ->
  // setPreCampaignData -> initialValues, which can briefly disagree with
  // itself while hydrating — reading the name directly from jobConfigList
  // here keeps the page heading stable instead of flickering between
  // values during that hydration.
  const currentJobConfig = useMemo(
    () => jobConfigList.find((config: any) => config.jobConfigId === jobId),
    [jobConfigList, jobId]
  );
  const displayStudyName =
    currentJobConfig?.name || preCampaignData.geoLiftStudyName || JobName;

  // The Experiments list page (pages/experiments/index.tsx) fetches job configs
  // into local component state, not Redux — so jobConfigList is empty when
  // navigating here directly. PreCampaign's hydration effect (and the
  // columnMappings effect below) both read from Redux jobConfigList, so make
  // sure it's populated for view/edit mode regardless of how the user arrived.
  //
  // Checking `currentJobConfig` (not just jobConfigList.length === 0) matters
  // for jobs reached via a fresh notification/toast within the same session:
  // if jobConfigList was already cached from an earlier visit *before* this
  // job existed, the list is non-empty but still doesn't contain this job —
  // campaignStartDate/bauSpent would never hydrate without this refetch.
  useEffect(() => {
    if ((isViewMode || isEditMode) && jobId && !currentJobConfig) {
      dispatch(fetchJobConfigList({ limit: 200, offset: 0 }));
    }
  }, [isViewMode, isEditMode, jobId, currentJobConfig]);

  useEffect(() => {
    const isEditOrViewMode = jobId && (edit === "true" || id);
    if (!isEditOrViewMode || jobConfigList.length === 0) return;

    const jobConfig = jobConfigList.find((config: any) => config.jobConfigId === jobId);
    if (!jobConfig) return;

    // Load column mappings once
    if (!columnMappingsLoaded && jobConfig?.dataMappingConfig?.mappings?.geoLift?.columnMapping) {
      const mapping = jobConfig.dataMappingConfig.mappings.geoLift.columnMapping;
      const kpiColumn = mapping.kpi_variables?.[0] || "Y";
      setColumnMappings({
        dateColumn: mapping.date_variables?.[0] || "date",
        locationColumn: mapping.location_variables?.[0] || "location",
        kpiColumn: kpiColumn.toLowerCase() === 'y' ? 'Y' : kpiColumn,
      });
      setColumnMappingsLoaded(true);
    }

    // Capture the baseline from the DB record once — do it as soon as the
    // config is available, before any form-state propagation can interfere.
    if (!initialJobConfig) {
      setInitialJobConfig(jobConfig);
    }
  }, [jobId, edit, id, jobConfigList, columnMappingsLoaded, initialJobConfig]);

  // Compare current form state against the DB baseline captured above.
  // Reading from initialJobConfig (Redux/DB) instead of snapshotting form state
  // eliminates the race where bauSpent/statSigThreshold propagate one render
  // after markets/campaignDate, causing false "changed" positives.
  const hasDataChanged = useMemo(() => {
    if (!isEditMode || !initialJobConfig) return false;

    const p = initialJobConfig.modelParameterConfig?.parameters || {};

    if (preCampaignData.geoLiftStudyName !== (initialJobConfig.name || "")) return true;

    const currentDate = campaignDate?.format("YYYY-MM-DD") || null;
    if (currentDate !== (p.campaignStartDate || null)) return true;

    if (scmType !== (p.scmType || "single")) return true;
    if (isSales !== (p.isSales ?? true)) return true;

    if (Number(preCampaignData.bauSpent) !== (p.bauSpent ?? 0)) return true;
    if (Number(preCampaignData.statSigThreshold) !== (p.statSigThreshold ?? DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD)) return true;

    const currentMarkets = [...selectedMarketsIncludes].sort().join(",");
    const initialMarkets = [...(Array.isArray(p.includeMarket) ? p.includeMarket : [])].sort().join(",");
    if (currentMarkets !== initialMarkets) return true;

    return false;
  }, [
    isEditMode, initialJobConfig,
    preCampaignData.geoLiftStudyName, preCampaignData.bauSpent, preCampaignData.statSigThreshold,
    campaignDate, scmType, isSales, selectedMarketsIncludes,
  ]);

  // True when ONLY the test name changed — every other editable field matches the DB baseline.
  const isOnlyNameChanged = useMemo(() => {
    if (!isEditMode || !initialJobConfig) return false;

    const p = initialJobConfig.modelParameterConfig?.parameters || {};

    if (preCampaignData.geoLiftStudyName === (initialJobConfig.name || "")) return false;

    const currentDate = campaignDate?.format("YYYY-MM-DD") || null;
    if (currentDate !== (p.campaignStartDate || null)) return false;
    if (scmType !== (p.scmType || "single")) return false;
    if (isSales !== (p.isSales ?? true)) return false;
    if (Number(preCampaignData.bauSpent) !== (p.bauSpent ?? 0)) return false;
    if (Number(preCampaignData.statSigThreshold) !== (p.statSigThreshold ?? DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD)) return false;

    const currentMarkets = [...selectedMarketsIncludes].sort().join(",");
    const initialMarkets = [...(Array.isArray(p.includeMarket) ? p.includeMarket : [])].sort().join(",");
    if (currentMarkets !== initialMarkets) return false;

    return true;
  }, [
    isEditMode, initialJobConfig,
    preCampaignData.geoLiftStudyName, preCampaignData.bauSpent, preCampaignData.statSigThreshold,
    campaignDate, scmType, isSales, selectedMarketsIncludes,
  ]);

  // Keep postTestData.isSales in sync when isSales is loaded from Redux
  useEffect(() => {
    if (postTestData) {
      setPostTestData((prev: any) => prev ? { ...prev, isSales } : prev);
    }
  }, [isSales]);

  const handleFormSubmit = async (formData: Record<string, any>, options?: { skipJobRun?: boolean }) => {
    const isPreCampaignData =
      formData.fileList && formData.buyType && formData.geoLiftStudyName;
    if (!isPreCampaignData) {
      if (formData.includeMarket && Array.isArray(formData.includeMarket)) {
        formData.includeMarket = formData.includeMarket.join(";;;");
      }
      if (formData.excludeMarket && Array.isArray(formData.excludeMarket)) {
        formData.excludeMarket = formData.excludeMarket.join(";;;");
      }
    }

    try {
      // Use actual user column names from columnMappings state
      const locationVariable = columnMappings.locationColumn || "location";
      // Always normalize KPI variable to uppercase 'Y' to match CSV column name
      const rawKpiVariable = columnMappings.kpiColumn || "Y";
      const kpiVariable = rawKpiVariable.toLowerCase() === 'y' ? 'Y' : rawKpiVariable;
      const dateVariable = columnMappings.dateColumn || "date";

      console.log("Column mappings being used:", {
        dateVariable,
        locationVariable,
        kpiVariable,
        originalKpiColumn: columnMappings.kpiColumn
      });

      const fileSource = isPreCampaignData
        ? formData.fileList[0]
        : preCampaignData.fileList[0];
      const jobId = id === undefined ? uuidv4() : id;
      setJobId(jobId);

      // Only parse CSV if we have a file (new job or file changed)
      let durationType = "weeks"; // Default value
      if (fileSource) {
        const csvData = await new Promise<any[]>((resolve) => {
          Papa.parse(fileSource, {
            complete: (results) => resolve(results.data),
            header: true,
          });
        });
        const locations = csvData
          .map((row) => row[locationVariable])
          .filter((value, index, self) => self.indexOf(value) === index);
        const firstLocation = locations[0];
        const locationData = csvData.filter(
          (row) => row[locationVariable] === firstLocation
        );
        const dates = locationData.map((row) => {
          const dateStr = row[dateVariable];
          const format = "YYYY-MM-DD";
          const jsFormat = format
            .replace(/%Y/g, "YYYY")
            .replace(/%y/g, "YY")
            .replace(/%m/g, "MM")
            .replace(/%d/g, "DD")
            .replace(/%H/g, "HH")
            .replace(/%M/g, "mm")
            .replace(/%S/g, "ss");
          return dayjs(dateStr, jsFormat).toDate();
        });
        const sortedDates = dates
          .filter((d) => !isNaN(d.getTime()))
          .sort((a, b) => a.getTime() - b.getTime());
        let totalDiff = 0;
        for (let i = 1; i < sortedDates.length; i++) {
          totalDiff +=
            (sortedDates[i].getTime() - sortedDates[i - 1].getTime()) /
            (1000 * 60 * 60 * 24);
        }
        const avgDiffDays = totalDiff / (sortedDates.length - 1);
        durationType = Math.abs(avgDiffDays - 7) < 1 ? "weeks" : "days";
      }
      const studyName = isPreCampaignData
        ? formData.geoLiftStudyName
        : preCampaignData.geoLiftStudyName;
      const buyTypeValue = "national";

      const payload = {
        jobConfigId: jobId,
        name: studyName,
        jobType: JobType.GeoLift,
        is_ai_qc_enabled: preCampaignData.is_ai_qc_enabled,
        tenantId: getTenantId(),
        dataSourceConfig: {
          dataSourceType: "FILE_CSV",
        },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            geoLift: {
              columnMapping: {
                date_variables: [dateVariable],
                kpi_variables: [kpiVariable],
                location_variables: [locationVariable],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            dateFormat: "%Y-%m-%d",
            scmType: scmType,
            isSales,
            ...((campaignDate || preCampaignData.campaignStartDate) && {
              campaignStartDate: campaignDate ? campaignDate.format("YYYY-MM-DD") : preCampaignData.campaignStartDate,
            }),
            durationType: durationType,
            ...(isPreCampaignData ? {} : formData),
            buyType: buyTypeValue,
            ...(selectedMarketsIncludes.length > 0
              ? { includeMarket: selectedMarketsIncludes }
              : []),
            bauSpent: preCampaignData.bauSpent,
            statSigThreshold: preCampaignData.statSigThreshold,
          },
        },
        // Preserve the existing modelOutputConfig (which holds outputData like
        // aggregateLift, ROAS, etc.) so an edit doesn't wipe out computed results.
        modelOutputConfig: (() => {
          const existing = jobConfigList.find((c: any) => c.jobConfigId === jobId);
          return existing?.modelOutputConfig || {};
        })(),
      };
      // Detect job mode: new, edit, or view
      const modeParam = searchParams.get("mode");
      const isEditMode = edit === "true";
      const isNewJob = !!modeParam && !isEditMode; // New job has mode but NOT edit=true
      const isViewMode = !modeParam && !isEditMode && id && jobId; // View mode: no mode, no edit, but has id

      console.log("handleFormSubmit - Job type detection:", {
        isNewJob,
        isEditMode,
        isViewMode,
        modeParam,
        edit,
        id,
        jobId
      });

      // View mode: Don't submit anything, just return
      if (isViewMode) {
        console.log("View mode detected - no submission");
        return;
      }

      // Edit mode: update job config and optionally trigger job run
      if (isEditMode) {
        console.log("Edit mode detected - updating job config", options?.skipJobRun ? "(name-only, no re-run)" : "and triggering job run");

        await axiosInstance.put(`/api/jobConfig/${jobId}`, payload);

        // Upload file if user re-uploaded CSV
        if (fileSource) {
          const geoLiftFilePath = jobId + "/input/geo_lift_data.csv";
          await uploadFileToS3(fileSource, geoLiftFilePath);
        }

        if (!options?.skipJobRun) {
          await axiosInstance.post(
            "/api/jobRun",
            { jobConfigId: jobId },
            {
              headers: {
                Authorization: "Bearer " + localStorage.getItem("authToken"),
              },
            }
          );
          console.log("Job run triggered successfully in edit mode");
        }
        return;
      }

      // New job creation flow
      if (isNewJob) {
        console.log("New job detected - creating job config and triggering job run");

        // Only upload file if it exists (CSV upload case)
        // If fileSource is undefined, data was loaded from database and is already in S3
        if (fileSource) {
          const geoLiftFilePath = jobId + "/input/geo_lift_data.csv";
          await uploadFileToS3(fileSource, geoLiftFilePath);
          console.log("File uploaded to S3");
        } else {
          console.log("No file to upload - data was loaded from database");
        }

        await axiosInstance.post("/api/jobConfig", payload);
        await axiosInstance.post(
          "/api/jobRun",
          { jobConfigId: jobId },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );
        console.log("New job created successfully");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleMarketChange = (value: string) => {
    setSelectedMarket(value);
    setMarketData(allMarketData[value] || []);
  };
  const handlePreCampaignChange = (values: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string;
    totalSpent?: string;
    columnMappings?: { [key: string]: string | null }; // NEW: Accept mappings from PreCampaign
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
  }) => {
    setPreCampaignData(values); // Preserve all PreCampaign data

    // NEW: If column mappings are provided from PreCampaign modal, use them
    if (values.columnMappings) {
      const mappings = {
        dateColumn: values.columnMappings.date || "date",
        locationColumn: values.columnMappings.location || "location",
        kpiColumn: values.columnMappings.kpi || "y",
      };
      setColumnMappings(mappings);

      // Parse CSV with the confirmed mappings
      if (values.fileList.length > 0) {
        const file: any = values.fileList[0];
        if (file) {
          Papa.parse(file, {
            header: true,
            complete: (result) => {
              const { data } = result;
              const parsedData = data
                .filter(
                  (item: any) =>
                    item[mappings.dateColumn] && item[mappings.locationColumn]
                )
                .map((item: any) => ({
                  date: item[mappings.dateColumn],
                  location: item[mappings.locationColumn].trim(),
                  value: parseFloat(item[mappings.kpiColumn]) || 0,
                }));

              setCsvData(parsedData);
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
          });
        }
      }
    }
    // LEGACY: Keep old auto-detection logic as fallback (for backward compatibility)
    else if (values.fileList.length > 0) {
      const file: any = values.fileList[0];
      if (file) {
        Papa.parse(file, {
          header: true,
          complete: (result) => {
            const { data, meta } = result;
            const autoDetectedColumns = {
              dateColumn:
                meta.fields?.find((f) => f.toLowerCase().includes("date")) ||
                "date",
              locationColumn:
                meta.fields?.find((f) =>
                  f.toLowerCase().includes("location")
                ) || "location",
              kpiColumn:
                meta.fields?.find((f) =>
                  f.toLowerCase().match(/y|sales|revenue/)
                ) || "y",
            };

            setColumnMappings(autoDetectedColumns);

            const parsedData = data
              .filter(
                (item: any) =>
                  item[autoDetectedColumns.dateColumn] &&
                  item[autoDetectedColumns.locationColumn]
              )
              .map((item: any) => ({
                date: item[autoDetectedColumns.dateColumn],
                location: item[autoDetectedColumns.locationColumn].trim(),
                value: parseFloat(item[autoDetectedColumns.kpiColumn]) || 0,
              }));

            setCsvData(parsedData);
          },
          error: (error) => {
            console.error("CSV parsing error:", error);
            setCsvData([]);
          },
        });
      }
    } else {
      setCsvData([]);
    }
  };

  const handleJobCreated = (newJobId: string) => {
    setJobId(newJobId);
  };
  const handleNext = async () => {
    const isEvaluateMode = mode === "evaluate";
    const isViewingRecommendation =
      recommendationStatus === "recommendation_success";

    const modeParam = searchParams.get("mode");
    const isNewJob = !!modeParam;
    const isExistingJob = edit === "true" || (!isNewJob && id && jobId);

    const isOnUploadStep = steps[current]?.id === "upload";
    const isOnVerifyStep = steps[current]?.id === "verify";

    console.log("handleNext - Debug info:", {
      current,
      stepId: steps[current]?.id,
      isEvaluateMode,
      isNewJob,
      isExistingJob,
      hasDataChanged,
      modeParam,
      edit,
      id,
      jobId,
    });

    if (!isViewingRecommendation) {
      if (isOnUploadStep) {
        // In view mode just advance — never submit, never reset market data.
        if (isViewMode) {
          setCurrent(current + 1);
          scrollContentToTop();
          return;
        }

        const studyName = preCampaignData.geoLiftStudyName || "Geo Lift Study";

        // Edit mode: differentiate name-only vs full re-run
        if (isEditMode) {
          if (isOnlyNameChanged) {
            // Fast path — just persist the name, no pipeline re-run needed
            await handleFormSubmit(preCampaignData, { skipJobRun: true });
            message.success(`"${studyName}" updated successfully.`);
          } else {
            // Parameters changed — update config and re-run the pipeline
            await handleFormSubmit(preCampaignData);
            setAllMarketData({});
            setMarketData([]);
            setRefreshMarketData(prev => prev + 1);
            message.success(`"${studyName}" updated successfully — we'll notify you when it's ready.`);
          }
          navigate("/experiments");
          return;
        }

        // New job creation flow
        if (isEvaluateMode && isExistingJob && !hasDataChanged) {
          console.log("No changes detected, skipping resubmission");
        } else {
          console.log("Submitting form with changes");
          await handleFormSubmit(preCampaignData);
          setAllMarketData({});
          setMarketData([]);
          setRefreshMarketData(prev => prev + 1);
        }

        message.success(`"${studyName}" submitted successfully — we'll notify you when it's ready.`);
        navigate("/experiments");
        return;
      } else if (isOnVerifyStep && !isEvaluateMode && verifyTestInputRef.current) {
        verifyTestInputRef.current.submitForm();
      }
    }

    if (current < steps.length - 1) {
      setCurrent(current + 1);
      scrollContentToTop();
    }
  };
  const handlePrev = () => {
    setCurrent((current) => Math.max(current - 1, 0)); // Ensure it doesn't go below 0
    // Scroll to top when moving to previous step
    scrollContentToTop();
  };

  const allSteps = [
    {
      id: 'upload',
      title: "Data Connect/Upload",
      content: (
        <PreCampaign
          ref={preCampaignRef}
          jobConfigId={jobId}
          fileData={fileData}
          onValuesChange={handlePreCampaignChange}
          initialValues={preCampaignData}
          setFileUploaded={setFileUploaded}
          setCampaignDate={setCampaignDate}
          mode={mode}
          setSelectedMarketsIncludes={setSelectedMarketsIncludes}
          selectedMarketsIncludes={selectedMarketsIncludes}
          scmType={scmType}
          setScmType={setScmType}
          isSales={isSales}
          setIsSales={setIsSales}
          viewMode={isViewMode}
        />
      ),
    },
    {
      id: 'verify',
      title: "Verify test inputs",
      content: (
        <VerifyTestInput
          ref={verifyTestInputRef}
          jobConfigId={jobId}
          preCampaignData={preCampaignData}
          isNationalBuy={preCampaignData.buyType === "national"}
          csvData={csvData}
          dateColumn={columnMappings.dateColumn}
          locationColumn={columnMappings.locationColumn}
          onFormSubmit={handleFormSubmit}
          kpiColumn={columnMappings.kpiColumn}
          onJobCreated={handleJobCreated}
        />
      ),
    },
    {
      id: 'power',
      title: "Power Analysis",
      content: (
        <ViewTestAndControlMarket
          testMarkets={testMarkets}
          selectedMarket={selectedMarket}
          jobConfigId={jobId}
          marketData={marketData}
          preCampaignData={preCampaignData}
          onMarketChange={handleMarketChange}
          onTestMarketsChange={setTestMarkets}
          buyType={buyType}
          mode={mode}
          refreshTrigger={refreshMarketData}
          isSales={isSales}
        />
      ),
    },
    {
      id: 'post',
      title: "Upload Post campaign data",
      content: (
        <div>
          <PostTestControlMarketSelection
            jobConfigId={jobId}
            setPostTestData={setPostTestData}
            onNext={handleNext}
          />
        </div>
      ),
    },
    {
      id: 'results',
      title: "Incrementality Results",
      content: (
        <ViewResults
          resultData={postTestData}
          jobConfigId={jobId}
          setPostTestData={setPostTestData}
          mode={mode}
          isSales={isSales}
        />
      ),
    },
    {
      id: 'ai',
      title: "AI Insights",
      content: (
        <AIAgentInsights
          jobId={jobId}
          isSales={isSales}
        />
      ),
    }

  ];

  const steps = useMemo(() => {
    const isEvaluateOrNational = mode === "evaluate" || resolvedBuyType === "national";

    // Define logic for which step IDs are allowed per mode
    let allowedIds: string[];

    if (isEvaluateOrNational) {
      allowedIds = ['upload', 'power', 'results', 'ai'];
    } else {
      allowedIds = ['upload', 'power', 'results', 'ai'];
    }

    return allSteps.filter(step => allowedIds.includes(step.id));
  }, [mode, resolvedBuyType, allSteps]);

  // ── NEW: Conversational create flow ──────────────────────────────────────
  // When creating a new study, show the chat + artifacts 2-panel layout.
  // The old form wizard is preserved only for view / edit of existing studies.
  if (isCreateMode && !isManualCreate) {
    return (
      <div className="w-full" style={{ height: "100%" }}>
        <SyntheticControlStudy
          initialSessionId={null}
          onJobConfigCreated={(jobConfigId) => {
            // Navigate to view mode once the job is created
            navigate(`/experiments/geo-lift/${jobConfigId}`, {
              state: { jobConfigId },
            });
          }}
        />
      </div>
    );
  }

  if (isViewMode && resolvedBuyType === undefined) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 16, background: "var(--bg-base)", fontFamily: "var(--font-sans)" }}>
        <Spin size="large" />
        <span style={{ color: "var(--text-secondary)", fontSize: 13, fontFamily: "var(--font-sans)" }}>Loading…</span>
      </div>
    );
  }


  return (
    <div className="w-full" style={{ height: "100%", display: "flex", flexDirection: "column", background: "var(--bg-base)", fontFamily: "var(--font-sans)" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Stepper Section */}
        <div
          className="pr-4 pl-6 overflow-y-auto"
          style={{ width: "25%", flexShrink: 0, height: "100%", background: "var(--bg-surface)", borderRight: "1px solid var(--border-default)" }}
        >
          <button
            onClick={() => navigate("/experiments")}
            style={{
              marginTop: 16, marginBottom: 8, padding: "7px 14px", borderRadius: "var(--radius)",
              background: "none", color: "var(--text-tertiary)",
              border: "1px solid var(--border-default)", fontSize: 12,
              cursor: "pointer", fontFamily: "var(--font-sans)",
            }}
          >
            ← Back to list
          </button>

          {mode === "plan" ? (
            <h2 className="text-2xl font-semibold mt-5 mb-6" style={{ color: "var(--text-primary)" }}>
              {displayStudyName || "Geo Lift Study"}
            </h2>
          ) : (
            <h2 className="text-2xl font-semibold mt-5 mb-6" style={{ color: "var(--text-primary)" }}>
              {displayStudyName ||
                // In view/edit mode the real study name is fetched async via
                // jobConfigList — show a muted placeholder instead of a
                // generic fallback so the heading doesn't flash between two
                // different "real-looking" titles before the name arrives.
                ((isViewMode || isEditMode) ? (
                  <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>
                    Loading study name…
                  </span>
                ) : (
                  "Synthetic Control Test"
                ))}
            </h2>
          )}

          <Steps
            direction="vertical"
            current={current}
            onChange={(value) => {
              setCurrent(value);
              scrollContentToTop();
            }}
            items={steps.map((step) => ({
              title: step.title,
              disabled: true,

              // description: step.description,
            }))}
            className="[&_.ant-steps-item]:mb-12 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[100px] [&_.ant-steps-item-tail]:!w-[3px] [&_.ant-steps-item-icon]:!text-lg [&_.ant-steps-item-icon]:!w-8 [&_.ant-steps-item-icon]:!h-8 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-[1rem]"
          />
        </div>

        {/* Content Section */}
        <div
          ref={contentContainerRef}
          style={{
            flex: 1,
            borderLeft: "1px solid var(--border-subtle)",
            overflow: "auto",
            padding: "24px 16px",
            height: "100%",
            boxSizing: "border-box",
            background: "var(--bg-base)",
            color: "var(--text-primary)",
          }}
        >
          <div style={{ paddingBottom: 72 }}>{steps[current].content}</div>
        </div>
      </div>
      {/* Footer — in-flow (not fixed) so it doesn't cover content */}
      <div className="p-4 flex justify-between" style={{ flexShrink: 0, background: "var(--bg-surface)", borderTop: "1px solid var(--border-subtle)" }}>
        <div>
          {current > 0 && (
            <Button
              onClick={handlePrev}
              size="large"
              className="mr-2"
              icon={<span className="mr-1">←</span>}
            >
              Previous
            </Button>
          )}
        </div>

        <div>
          {(isViewMode
            ? (steps[current]?.id !== 'ai')
            : current < steps.length - 1) && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              disabled={isNextDisabled()}
              icon={<span className="ml-1">→</span>}
            >
              {getNextButtonLabel()}
            </Button>
          )}
          {(isViewMode
            ? steps[current]?.id === 'ai'
            : current === steps.length - 1) && (
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/experiments")}
            >
              Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeoLiftStudy;
